// Sign-in accepts the credentials every account has and the optional second-factor code an account may
// have enabled. It intentionally gives one response to ordinary authentication refusal: separating a
// username, password or code error would reveal more about an account than a sign-in screen should know.
// For the same reason the code field opens only when the person says they use an authenticator app, never
// because of what the server answered; the device remembers that choice.

import { type SignIn } from '@holydeck/contracts/accounts';
import { SESSION_PATH, SIGN_IN_REFUSED } from '@holydeck/contracts/sessions';
import { CHALLENGE_SECONDS } from '@holydeck/contracts/webauthn';
import { useEffect, useId, useRef, useState } from 'preact/hooks';

import { reloadAsAnotherAccount } from '../account-switch.js';
import { apiUnreachable, serverVersion, session } from '../app-state.js';
import { SignInMarks } from '../components/church-mark.js';
import { FormField } from '../components/form-field.js';
import { Banner } from '../components/kit/banner.js';
import { Icon } from '../components/icon.js';
import { LanguageSwitch } from '../components/language-switch.js';
import { browserStorage, type PreferenceStorage } from '../device-preferences.js';
import { fieldErrors } from '../form-errors.js';
import { t } from '../i18n.js';
import { wrongAddressText } from '../passkey-wrong-address.js';
import {
  PASSKEY_CANCELLED,
  assertionOptionsOf,
  browserWebAuthn,
  conditionalMediationAvailable,
  passkeyCapabilities,
  startAssertion,
  type AssertedPasskey,
  type WebAuthnLike,
} from '../passkey.js';
import { boot, request } from '../request.js';
import { confirmLeaving } from '../router.js';

import type { JSX, TargetedEvent } from 'preact';

// Refresh before the server can charge an address for spending an expired challenge.
const PASSKEY_CHALLENGE_REFRESH_MARGIN_SECONDS = 20;

const CODE_CHOICE_KEY = 'holydeck.signInCode';

const readCodeChoice = (storage: PreferenceStorage): boolean => {
  try {
    return storage.getItem(CODE_CHOICE_KEY) === 'on';
  } catch {
    return false;
  }
};

const writeCodeChoice = (storage: PreferenceStorage, uses: boolean): void => {
  try {
    storage.setItem(CODE_CHOICE_KEY, uses ? 'on' : 'off');
  } catch {
    // Storage refused: this page still keeps the choice.
  }
};

const selectPassword = (): void => {
  const field = document.getElementById('sign-in-password');
  if (field instanceof HTMLInputElement) {
    field.focus();
    field.select();
  }
};

type ConditionalPasskeyRequest = {
  readonly controller: AbortController;
  readonly drawnAt: number;
  refresh: ReturnType<typeof setTimeout> | undefined;
};

export interface SignInPageProps {
  readonly next: string | undefined;
  readonly notice?: 'claim-sign-in-refused';
  /** Signing in from a tab that already holds a session adds the account as a new slot. */
  readonly add?: boolean;
  readonly browser?: WebAuthnLike;
  /** Where the device remembers whether this person uses an authenticator app. */
  readonly storage?: PreferenceStorage;
}

/** The session-opening form, with an optional safe destination supplied by the router. */
export function SignInPage({ next, notice, add = false, browser = browserWebAuthn(), storage = browserStorage }: SignInPageProps): JSX.Element {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [usesCode, setUsesCode] = useState(() => readCodeChoice(storage));
  const usesCodeId = useId();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [passkeyOffered, setPasskeyOffered] = useState(false);
  const stopConditional = useRef<() => void>(() => undefined);
  const restartConditional = useRef<() => void>(() => undefined);
  const attemptInFlight = useRef(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  // The conditional ceremony's mount-only effect keeps the first render's `sendAssertion`; it reads the choice here.
  const keepSignedInChoice = useRef(true);
  const keepSignedInId = useId();
  const keepSignedInHintId = useId();

  const chooseKeepSignedIn = (checked: boolean): void => {
    keepSignedInChoice.current = checked;
    setKeepSignedIn(checked);
  };

  const chooseUsesCode = (checked: boolean): void => {
    setUsesCode(checked);
    writeCodeChoice(storage, checked);
  };

  useEffect(() => {
    void passkeyCapabilities(browser).then(({ supported }) => setPasskeyOffered(supported));
    // What the browser can do is fixed per mount.
  }, []);

  // The server made the added account the active slot, so this tab is now that account's and starts
  // over as it; an ordinary sign-in returns through boot, as a restoring session does.
  const signedIn = async (): Promise<void> => {
    if (add) {
      reloadAsAnotherAccount();
    } else {
      await boot();
    }
  };

  const wrongAddressKeys = { address: 'signIn.passkeyWrongAddress', domain: 'signIn.passkeyWrongDomain' } as const;

  const sendAssertion = async (credential: AssertedPasskey): Promise<boolean> => {
    // The server reads the ceremony fields nested under `response`, as the browser returned them.
    const { id, rawId, type, response } = credential;
    const assertion = { id, rawId, type, response };
    const result = await request(SESSION_PATH, { method: 'POST', csrf: '', body: { keepSignedIn: keepSignedInChoice.current, passkey: { step: 'assertion', assertion } } });
    if (!result.ok) {
      setError(wrongAddressText(result, wrongAddressKeys) ?? t('signIn.passkeyRefused'));
      return false;
    }

    await signedIn();
    return true;
  };

  useEffect(() => {
    let active = true;
    let current: ConditionalPasskeyRequest | undefined;

    const clear = (request: typeof current): void => {
      if (request?.refresh !== undefined) {
        clearTimeout(request.refresh);
      }

      if (current === request) {
        current = undefined;
      }
    };

    const stop = (): void => {
      const pending = current;
      clear(pending);
      pending?.controller.abort();
    };

    const start = (): void => {
      if (attemptInFlight.current) {
        return;
      }

      stop();
      const pending: ConditionalPasskeyRequest = { controller: new AbortController(), drawnAt: Date.now(), refresh: undefined };
      current = pending;
      void (async () => {
        let wasSubmitting = false;
        try {
          const challenge = await request(SESSION_PATH, { method: 'POST', csrf: '', body: { passkey: { step: 'challenge' } } });
          if (!active || current !== pending || pending.controller.signal.aborted || !challenge.ok) {
            clear(pending);
            return;
          }

          const options = assertionOptionsOf(challenge.data);
          if (options === undefined) {
            clear(pending);
            return;
          }

          if (Date.now() - pending.drawnAt >= (CHALLENGE_SECONDS - PASSKEY_CHALLENGE_REFRESH_MARGIN_SECONDS) * 1_000) {
            start();
            return;
          }

          const refreshIn = (CHALLENGE_SECONDS - PASSKEY_CHALLENGE_REFRESH_MARGIN_SECONDS) * 1_000 - (Date.now() - pending.drawnAt);
          pending.refresh = setTimeout(start, Math.max(0, refreshIn));
          const ceremony = await startAssertion(browser, options, {
            mediation: 'conditional',
            signal: pending.controller.signal,
          });
          if (!active || current !== pending || pending.controller.signal.aborted || !ceremony.ok) {
            clear(pending);
            return;
          }

          if (add && !confirmLeaving(t('app.account.switchUnsaved'))) {
            start();
            return;
          }

          setSubmitting(true);
          wasSubmitting = true;
          const signed = await sendAssertion(ceremony.credential);
          if (!signed && active && current === pending) {
            setSubmitting(false);
            start();
          }
        } catch {
          clear(pending);
        } finally {
          if (wasSubmitting && active && current === pending) {
            setSubmitting(false);
          }
        }
      })();
    };

    const refreshWhenVisible = (): void => {
      if (
        !document.hidden &&
        current !== undefined &&
        Date.now() - current.drawnAt >= (CHALLENGE_SECONDS - PASSKEY_CHALLENGE_REFRESH_MARGIN_SECONDS) * 1_000
      ) {
        start();
      }
    };

    void conditionalMediationAvailable(browser).then((available) => {
      if (!active || !available) {
        return;
      }

      stopConditional.current = stop;
      restartConditional.current = start;
      document.addEventListener('visibilitychange', refreshWhenVisible);
      start();
    });

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      stop();
      stopConditional.current = () => undefined;
      restartConditional.current = () => undefined;
    };
    // This browser and the page's account mode are fixed for one mount.
  }, []);

  const submit = async (event: TargetedEvent<HTMLFormElement, SubmitEvent>): Promise<void> => {
    event.preventDefault();
    // The server makes an added account the active one as soon as it answers, so adding asks first, as a switch does.
    if (add && !confirmLeaving(t('app.account.switchUnsaved'))) {
      return;
    }

    attemptInFlight.current = true;
    stopConditional.current();
    setSubmitting(true);
    setError(undefined);
    let signed = false;
    try {
      const credentials: SignIn = usesCode && code !== '' ? { name, password, code, keepSignedIn } : { name, password, keepSignedIn };
      const result = await request(SESSION_PATH, { method: 'POST', csrf: '', body: credentials });
      if (!result.ok) {
        if (result.code === SIGN_IN_REFUSED) {
          setError(t('signIn.refused'));
          selectPassword();
        } else {
          setError(fieldErrors(result, ['name', 'password', 'code']).other);
        }

        return;
      }

      await signedIn();
      signed = true;
    } finally {
      setSubmitting(false);
      attemptInFlight.current = false;
      if (!signed) {
        restartConditional.current();
      }
    }
  };

  // One message for every refusal, as the password form gives: which step refused says nothing a person can act
  // on. The one exception is an address passkeys do not work on, which says where they do. A dismissed prompt is
  // the person's own choice and gets no message at all.
  const signInWithPasskey = async (): Promise<void> => {
    if (add && !confirmLeaving(t('app.account.switchUnsaved'))) {
      return;
    }

    attemptInFlight.current = true;
    stopConditional.current();
    setSubmitting(true);
    setError(undefined);
    let signed = false;
    try {
      const challenge = await request(SESSION_PATH, { method: 'POST', csrf: '', body: { passkey: { step: 'challenge' } } });
      if (!challenge.ok) {
        setError(wrongAddressText(challenge, wrongAddressKeys) ?? t('signIn.passkeyRefused'));
        return;
      }

      const options = assertionOptionsOf(challenge.data);
      if (options === undefined) {
        setError(t('signIn.passkeyRefused'));
        return;
      }

      const ceremony = await startAssertion(browser, options);
      if (!ceremony.ok) {
        if (ceremony.code !== PASSKEY_CANCELLED) {
          setError(t('signIn.passkeyRefused'));
        }

        return;
      }

      signed = await sendAssertion(ceremony.credential);
    } finally {
      setSubmitting(false);
      attemptInFlight.current = false;
      if (!signed) {
        restartConditional.current();
      }
    }
  };

  // Signed out, the page is one glass card on the aurora light, the language switch at its top; a signed-in tab adding
  // an account keeps its shell's own ground, and the language switch in its account menu. The password's Sign in is the one
  // gold action; a passkey, where the browser has one, sits beside it as the secondary kind.
  return (
    <div class="sign-in-page">
      {add ? null : <div class="aurora-light" aria-hidden="true" />}
      <form class="card sign-in-card" noValidate aria-busy={submitting} onSubmit={submit}>
        {session.value == null ? <LanguageSwitch compact /> : null}
        <SignInMarks withCity />
        <h1>{t(add ? 'signIn.addTitle' : 'signIn.title')}</h1>
        {session.value === null && next !== undefined ? <Banner kind="info">{t('signIn.expired')}</Banner> : null}
        {session.value === null && apiUnreachable.value ? <Banner kind="warn">{t('signIn.serverUnreachable')}</Banner> : null}
        {notice === 'claim-sign-in-refused' ? <Banner kind="bad">{t('signIn.refused')}</Banner> : null}
        {error === undefined ? null : <Banner kind="bad" alert>{error}</Banner>}
        <FormField id="sign-in-name" label={t('signIn.name')} value={name} onInput={setName} autoComplete="username webauthn" required />
        <FormField
          id="sign-in-password"
          label={t('signIn.password')}
          type="password"
          value={password}
          onInput={setPassword}
          autoComplete="current-password"
          required
        />
        <div class="sign-in-code">
          <label for={usesCodeId}>
            <input id={usesCodeId} type="checkbox" checked={usesCode} onChange={(event) => chooseUsesCode(event.currentTarget.checked)} />
            {t('signIn.codeToggle')}
          </label>
          {usesCode ? (
            <FormField
              id="sign-in-code"
              label={t('signIn.code')}
              value={code}
              onInput={setCode}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellcheck={false}
            />
          ) : null}
        </div>
        <div class="sign-in-persistence">
          <label for={keepSignedInId}>
            <input
              id={keepSignedInId}
              type="checkbox"
              checked={keepSignedIn}
              onChange={(event) => chooseKeepSignedIn(event.currentTarget.checked)}
              aria-describedby={keepSignedInHintId}
            />
            {t('signIn.keepSignedIn')}
          </label>
          <p id={keepSignedInHintId}>{t('signIn.keepSignedInHint')}</p>
        </div>
        <div class="form-actions">
          <button type="submit" class="button-primary" disabled={submitting}>
            {t(submitting ? 'signIn.submitting' : 'signIn.submit')}
          </button>
          {passkeyOffered ? (
            <button type="button" class="button-secondary sign-in-passkey" onClick={() => void signInWithPasskey()} disabled={submitting}>
              <Icon name="fingerprint" />
              {t('signIn.passkey')}
            </button>
          ) : null}
        </div>
        <p class="sign-in-forgot">{t('signIn.forgot')}</p>
      </form>
      <p class="sign-in-watch">
        <a href="/watch" data-watch-link>
          <Icon name="broadcast" />
          {t('watch.title')}
        </a>
      </p>
      {session.value === null && serverVersion.value !== undefined ? (
        <footer class="app-version">{t('app.version', { version: `v${serverVersion.value}` })}</footer>
      ) : null}
    </div>
  );
}
